import { Product } from '@/types/products/product';
import { 
  Moon, 
  Phone, 
  Clock, 
  Globe, 
  AlertTriangle, 
  MessageSquare, 
  Users, 
  Building2, 
  Stethoscope, 
  Wrench,
  FileText,
  Settings,
  PhoneForwarded,
  Mail
} from 'lucide-react';

export const afterHours: Product = {
  meta: {
    id: 'after-hours',
    slug: 'after-hours',
    name: 'Help Genie After Hours',
    tagline: 'Never miss a call, even when you\'re closed',
    description: 'Professional after-hours call handling that captures caller details, routes emergencies, and ensures every customer feels heard—24/7.',
    icon: Moon,
    heroImage: '/home-page-imagery/replicate-prediction-3ymns27nhsrm80ctv0ptrjgqmw.avif',
    isFeatured: true
  },

  hero: {
    headline: 'Your Business Never Sleeps',
    subheadline: 'Intelligent after-hours call handling that captures leads, routes emergencies, and keeps customers happy while you rest',
  },

  features: [
    {
      id: 'business-hours',
      name: 'Business Hours Configuration',
      description: 'Set custom open/closed hours for each day of the week. Your Genie knows exactly when to take over.',
      icon: Clock,
      highlight: true
    },
    {
      id: 'caller-capture',
      name: 'Caller Information Capture',
      description: 'Collect name, phone number, and reason for calling. Get detailed summaries delivered to your inbox.',
      icon: MessageSquare,
      highlight: true
    },
    {
      id: 'emergency-redirect',
      name: 'Emergency Line Redirect',
      description: 'Route urgent calls to on-call staff or emergency numbers. Set priority levels and escalation rules.',
      icon: AlertTriangle,
      highlight: true
    },
    {
      id: 'country-routing',
      name: 'Country-Specific Routing',
      description: 'Different phone numbers for different regions. Handle international callers with local numbers.',
      icon: Globe,
      highlight: false
    },
    {
      id: 'escalation-rules',
      name: 'Smart Escalation Rules',
      description: 'Define priority levels and auto-escalation paths. Critical calls get routed immediately.',
      icon: PhoneForwarded,
      highlight: false
    },
    {
      id: 'transcription',
      name: 'Call Transcription',
      description: 'Full transcripts of every call delivered via email or SMS. Never miss a detail.',
      icon: FileText,
      highlight: false
    },
    {
      id: 'voicemail-alternative',
      name: 'Voicemail Alternative',
      description: 'Replace frustrating voicemail with a conversational Genie that engages callers naturally.',
      icon: Phone,
      highlight: false
    },
    {
      id: 'notifications',
      name: 'Instant Notifications',
      description: 'Get email or SMS alerts for every after-hours call. Stay informed without being on call.',
      icon: Mail,
      highlight: false
    }
  ],

  useCases: [
    {
      id: 'service-companies',
      title: 'Service Companies',
      description: 'Plumbers, electricians, HVAC—emergencies don\'t wait for business hours. Neither should your response.',
      icon: Wrench,
      benefits: [
        'Capture emergency service requests',
        'Route urgent calls to on-call technicians',
        'Schedule non-urgent appointments',
        'Build customer loyalty with responsiveness'
      ]
    },
    {
      id: 'small-business',
      title: 'Small Businesses',
      description: 'Can\'t afford overnight staff but don\'t want to miss opportunities? After Hours captures every lead while you sleep.',
      icon: Building2,
      benefits: [
        'Capture leads 24/7 without hiring night staff',
        'Professional image even outside business hours',
        'Wake up to detailed call summaries',
        'Never miss an opportunity again'
      ]
    },
    {
      id: 'medical-practices',
      title: 'Medical Practices',
      description: 'Handle after-hours patient calls with intelligent triage. Route emergencies to on-call physicians.',
      icon: Stethoscope,
      benefits: [
        'Triage calls based on urgency',
        'Route emergencies to on-call staff',
        'Collect patient information for follow-up',
        'HIPAA-compliant call handling'
      ]
    },
    {
      id: 'multi-timezone',
      title: 'Multi-Timezone Operations',
      description: 'Serving customers across time zones? Your Genie handles calls when your team is offline.',
      icon: Globe,
      benefits: [
        'Global coverage without global staffing',
        'Country-specific phone numbers',
        'Seamless handoffs between regions',
        'Consistent experience worldwide'
      ]
    }
  ],

  howItWorks: [
    {
      step: 1,
      title: 'Configure Your Hours',
      description: 'Set your business hours and define when After Hours mode activates.',
      icon: Settings
    },
    {
      step: 2,
      title: 'Set Up Routing Rules',
      description: 'Define emergency contacts, escalation paths, and notification preferences.',
      icon: PhoneForwarded
    },
    {
      step: 3,
      title: 'Customize Your Genie',
      description: 'Personalize greetings, questions to ask callers, and your brand voice.',
      icon: MessageSquare
    },
    {
      step: 4,
      title: 'Go Live',
      description: 'Your Genie takes over after hours, capturing calls and routing emergencies.',
      icon: Moon
    }
  ],

  integrations: [
    {
      name: 'Twilio',
      category: 'Phone System',
      description: 'Seamless integration with your existing phone infrastructure.',
    },
    {
      name: 'Email Providers',
      category: 'Notifications',
      description: 'Receive call summaries and transcripts via email.',
    },
    {
      name: 'SMS Gateways',
      category: 'Notifications',
      description: 'Get instant SMS alerts for priority calls.',
    },
    {
      name: 'CRM Systems',
      category: 'Lead Management',
      description: 'Automatically log calls and leads to your CRM.',
    }
  ],

  metrics: {
    setupTime: '1 hour',
    availability: '24/7',
    responseTime: 'Instant',
    satisfaction: '4.8/5'
  },

  demoConfig: {
    firstMessage: 'Thank you for calling. Our office is currently closed. I\'m the Help Genie after-hours assistant. How can I help you today?',
    systemPrompt: 'You are an after-hours call assistant. Your job is to greet callers professionally, collect their name and phone number, understand the reason for their call, and determine if it\'s an emergency that requires immediate escalation.',
    exampleScenarios: [
      'Customer calling about a water leak emergency',
      'New lead inquiring about services',
      'Existing customer with a non-urgent question'
    ]
  },

  faqs: [
    {
      question: 'How quickly does After Hours activate when my business closes?',
      answer: 'Instantly. Based on your configured business hours, your Genie seamlessly takes over the moment you close. No manual switching required.'
    },
    {
      question: 'Can I customize what information is collected from callers?',
      answer: 'Absolutely. You can configure the questions your Genie asks, from basic contact info to specific details relevant to your business.'
    },
    {
      question: 'How do emergency escalations work?',
      answer: 'You define what constitutes an emergency and who should be contacted. Your Genie can transfer calls directly, send SMS alerts, or follow a multi-step escalation path.'
    },
    {
      question: 'Will callers know they\'re talking to an AI?',
      answer: 'Your Genie is transparent when asked, but the conversation feels natural and professional. Most callers appreciate the immediate response over going to voicemail.'
    },
    {
      question: 'Can I use my existing business phone number?',
      answer: 'Yes. After Hours integrates with your existing phone system through Twilio. Your callers dial the same number they always have.'
    },
    {
      question: 'How do I receive call summaries?',
      answer: 'Choose your preferred method: email, SMS, or both. Each summary includes caller details, reason for calling, full transcript, and urgency level.'
    }
  ]
};
